import { DatePicker, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { Button } from '~/ui';
import styles from './CustomDatePicker.module.scss';
import cn from 'classnames';
import { useState } from 'react';
import i18n from 'i18next';

interface ICustomDatePickerBaseProps {
  title?: string;
  value: [Dayjs | null, Dayjs | null];
  disabledDate?: (current: Dayjs) => boolean;
  showPresets?: boolean;
  format?: string;
  placeholder?: [string, string];
  className?: string;
  disabled?: boolean;
  onBlur?: () => void;
}

interface ICustomDatePickerLegacyProps extends ICustomDatePickerBaseProps {
  onChange: (name: string, value: Dayjs | null) => void;
  onDatesChange?: never;
}

interface ICustomDatePickerSimpleProps extends ICustomDatePickerBaseProps {
  onChange?: never;
  onDatesChange: (dates: [Dayjs, Dayjs] | null) => void;
}

type ICustomDatePickerProps = ICustomDatePickerLegacyProps | ICustomDatePickerSimpleProps;

const { RangePicker } = DatePicker;

export const CustomDatePicker = ({
  title,
  value,
  disabled,
  onChange,
  onDatesChange,
  showPresets = false,
  disabledDate,
  format = 'DD.MM.YYYY',
  placeholder = [i18n.t('date_from'), i18n.t('date_by')],
  className,
  onBlur,
}: ICustomDatePickerProps) => {
  const [open, setOpen] = useState(false);

  const isPresetActive = (days: number) => {
    if (!value[0] || !value[1]) return false;
    const start = dayjs().subtract(days, 'day').startOf('day');
    const end = dayjs().endOf('day');
    return value[0].isSame(start, 'day') && value[1].isSame(end, 'day');
  };

  const handleDatesChange = (start: Dayjs | null, end: Dayjs | null) => {
    if (onDatesChange) {
      if (start && end) {
        onDatesChange([start, end]);
      } else {
        onDatesChange(null);
      }
    } else if (onChange) {
      onChange('start_date', start);
      onChange('end_date', end);
    }
  };

  const handlePresetClick = (days: number) => {
    const start = dayjs().subtract(days, 'day').startOf('day');
    const end = dayjs().endOf('day');
    handleDatesChange(start, end);
    setOpen(false);
  };

  return (
    <div className={styles.datePickerWrapper}>
      {title && <span className={styles.dateTitle}>{title}</span>}
      <RangePicker
        disabled={disabled}
        open={open}
        onOpenChange={setOpen}
        value={value}
        format={format}
        className={cn(styles.datePicker, className)}
        placeholder={placeholder}
        disabledDate={disabledDate}
        onBlur={onBlur}
        onChange={dates => {
          if (dates && dates[0] && dates[1]) {
            handleDatesChange(dates[0], dates[1]);
          } else {
            handleDatesChange(null, null);
          }
          setOpen(false);
        }}
        panelRender={panel =>
          showPresets ? (
            <div>
              <div className={styles.extraHeader}>
                <Space>
                  <Button
                    text="7 day"
                    className={cn(
                      styles.selectBtn,
                      isPresetActive(7) && styles.selectBtnActive
                    )}
                    onClick={() => handlePresetClick(7)}
                  />
                  <Button
                    text="30 days"
                    className={cn(
                      styles.selectBtn,
                      isPresetActive(30) && styles.selectBtnActive
                    )}
                    onClick={() => handlePresetClick(30)}
                  />
                  <Button
                    text="90 days"
                    className={cn(
                      styles.selectBtn,
                      isPresetActive(90) && styles.selectBtnActive
                    )}
                    onClick={() => handlePresetClick(90)}
                  />
                </Space>
              </div>
              {panel}
            </div>
          ) : (
            panel
          )
        }
      />
    </div>
  );
};
